let _viewerTz = "Australia/Adelaide";

export function setViewerTimezone(tz: string) { _viewerTz = tz; }
export function getViewerTimezone(): string { return _viewerTz; }

function asUTC(dateStr: string): Date {
  const s = dateStr.trim();
  if (s.includes("Z") || s.includes("+") || /\d{2}:\d{2}:\d{2}-/.test(s)) return new Date(s);
  return new Date(s.replace(" ", "T") + "Z");
}


export function fmtCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function fmtNumber(value: number): string {
  return new Intl.NumberFormat("en-AU").format(value);
}

export function fmtPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

// ── Absolute-instant formatters ──
// For values stored as UTC moments: created_at, last_login, sent_at, ingested_at.
// They shift the value into the viewer's timezone, so NEVER pass a venue-local
// wall-clock value (perf_date, or a date(transaction_date) sale date) through
// them — a 19:00 Darwin show came out as "6 Sep, 04:30 am". Use the fmtShow*
// family below for those.

export function fmtDate(dateStr: string, tz: string = _viewerTz): string {
  const d = asUTC(dateStr);
  return d.toLocaleDateString("en-AU", {
    timeZone: tz,
    day: "numeric",
    month: "short",
  });
}

export function fmtDateTime(dateStr: string, tz: string = _viewerTz): string {
  const d = asUTC(dateStr);
  return d.toLocaleString("en-AU", {
    timeZone: tz,
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtDateFull(dateStr: string, tz: string = _viewerTz): string {
  const d = asUTC(dateStr);
  return d.toLocaleString("en-AU", {
    timeZone: tz,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtTime(dateStr: string, tz: string = _viewerTz): string {
  const d = asUTC(dateStr);
  return d.toLocaleTimeString("en-AU", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtDayName(dateStr: string, tz: string = _viewerTz): string {
  const d = asUTC(dateStr);
  return d.toLocaleDateString("en-AU", {
    timeZone: tz,
    weekday: "short",
  });
}

export function fmtShowLabel(firstPerfDate: string | null, title: string): string {
  if (!firstPerfDate) return title;
  return `${firstPerfDate.slice(0, 10)} ${title}`;
}

// ── Show/performance date formatters ──
// These display dates AS-IS (venue local time) without timezone conversion.
// Feed and scrape data is stored in festival local time.

function parseShowDate(s: string): { y: number; m: number; d: number; h: number; min: number } {
  const [datePart, timePart] = s.split(/[T ]/);
  const [y, m, d] = (datePart || "").split("-").map(Number);
  const [h, min] = (timePart || "00:00").split(":").map(Number);
  return { y: y || 0, m: m || 0, d: d || 0, h: h || 0, min: min || 0 };
}

const MONTHS_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function fmtShowDate(dateStr: string): string {
  const p = parseShowDate(dateStr);
  return `${p.d} ${MONTHS_SHORT[p.m]}`;
}

export function fmtShowDateTime(dateStr: string): string {
  const p = parseShowDate(dateStr);
  const hh = String(p.h).padStart(2, "0");
  const mm = String(p.min).padStart(2, "0");
  return `${p.d} ${MONTHS_SHORT[p.m]}, ${hh}:${mm}`;
}

export function fmtShowDateFull(dateStr: string): string {
  const p = parseShowDate(dateStr);
  const d = new Date(p.y, p.m - 1, p.d);
  const day = DAYS_SHORT[d.getDay()] || "";
  const hh = String(p.h).padStart(2, "0");
  const mm = String(p.min).padStart(2, "0");
  return `${day}, ${p.d} ${MONTHS_SHORT[p.m]} ${p.y}, ${hh}:${mm}`;
}

export function fmtShowDayName(dateStr: string): string {
  const p = parseShowDate(dateStr);
  const d = new Date(p.y, p.m - 1, p.d);
  return DAYS_SHORT[d.getDay()] || "";
}
